import React, { useMemo, useState, useId } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { alpha } from '@mui/material/styles';
import {
  ChartContainer,
  ChartEmptyState,
  ChartTooltip,
  InteractiveLegend,
  chartAnimation,
  chartPalette,
  getSeriesColor,
  normalizeDataKeys,
  useChartTheme,
} from './chartTheme';

const InteractiveBarChart = ({
  data = [],
  dataKeys = [],
  title,
  subtitle,
  height = 320,
  showGrid = true,
  showLegend = true,
  horizontal = false,
  stacked = false,
  animated = true,
  colorByValue = false,
  xDataKey = 'name',
  valueFormatter,
  allowDecimals = true,
  ...props
}) => {
  const chartId = useId().replace(/:/g, '-');
  const chartTheme = useChartTheme(height);
  const [hiddenKeys, setHiddenKeys] = useState([]);

  const series = useMemo(
    () => normalizeDataKeys(dataKeys, data, [xDataKey]),
    [data, dataKeys, xDataKey],
  );

  const visibleSeries = useMemo(
    () => series.filter((item) => !hiddenKeys.includes(item.dataKey)),
    [hiddenKeys, series],
  );

  const maxValue = useMemo(() => {
    if (!colorByValue || !series.length) {
      return 0;
    }

    const firstKey = series[0].dataKey;
    return Math.max(...data.map((item) => Number(item?.[firstKey] || 0)), 0);
  }, [colorByValue, data, series]);

  const getColorByValue = (value) => {
    const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;

    if (percentage >= 70) {
      return chartPalette.success;
    }

    if (percentage >= 40) {
      return chartPalette.warning;
    }

    return chartPalette.error;
  };

  const toggleSeries = (dataKey) => {
    setHiddenKeys((current) => (
      current.includes(dataKey)
        ? current.filter((key) => key !== dataKey)
        : [...current, dataKey]
    ));
  };

  if (!data.length || !series.length) {
    return (
      <ChartContainer title={title} subtitle={subtitle}>
        <ChartEmptyState />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer title={title} subtitle={subtitle}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{
            ...chartTheme.margin,
            left: horizontal ? (chartTheme.isMobile ? 36 : 64) : chartTheme.margin.left,
            bottom: horizontal ? chartTheme.margin.bottom : (chartTheme.isMobile ? 18 : 8),
          }}
          barCategoryGap={chartTheme.isMobile ? '22%' : '28%'}
          {...props}
        >
          <defs>
            {series.map((item, index) => {
              const color = getSeriesColor(index, item.color);

              return (
                <linearGradient
                  key={item.dataKey}
                  id={`${chartId}-${item.dataKey}-bar`}
                  x1="0"
                  y1="0"
                  x2={horizontal ? '1' : '0'}
                  y2={horizontal ? '0' : '1'}
                >
                  <stop offset="0%" stopColor={alpha(color, 0.95)} />
                  <stop offset="100%" stopColor={alpha(color, 0.42)} />
                </linearGradient>
              );
            })}
          </defs>

          {showGrid && (
            <CartesianGrid
              stroke={chartTheme.palette.grid}
              strokeDasharray="4 4"
              vertical={horizontal}
              horizontal={!horizontal}
            />
          )}

          {horizontal ? (
            <>
              <XAxis
                type="number"
                stroke={chartTheme.palette.axis}
                tickCount={chartTheme.yTickCount}
                tickFormatter={valueFormatter}
                tick={{
                  fill: chartTheme.palette.textMuted,
                  fontSize: chartTheme.axisFontSize,
                  fontFamily: chartTheme.fontFamily,
                }}
              />
              <YAxis
                type="category"
                dataKey={xDataKey}
                stroke={chartTheme.palette.axis}
                width={chartTheme.isMobile ? 70 : 110}
                tick={{
                  fill: chartTheme.palette.textMuted,
                  fontSize: chartTheme.axisFontSize,
                  fontFamily: chartTheme.fontFamily,
                }}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey={xDataKey}
                type="category"
                stroke={chartTheme.palette.axis}
                interval="preserveStartEnd"
                minTickGap={chartTheme.isMobile ? 24 : 16}
                tick={{
                  fill: chartTheme.palette.textMuted,
                  fontSize: chartTheme.axisFontSize,
                  fontFamily: chartTheme.fontFamily,
                }}
                tickMargin={8}
              />
              <YAxis
                type="number"
                stroke={chartTheme.palette.axis}
                width={chartTheme.isMobile ? 32 : 44}
                tickCount={chartTheme.yTickCount}
                tickFormatter={valueFormatter}
                allowDecimals={allowDecimals}
                tick={{
                  fill: chartTheme.palette.textMuted,
                  fontSize: chartTheme.axisFontSize,
                  fontFamily: chartTheme.fontFamily,
                }}
              />
            </>
          )}

          <Tooltip
            cursor={{ fill: chartTheme.palette.cursor }}
            content={<ChartTooltip valueFormatter={valueFormatter} />}
          />

          {visibleSeries.map((item, index) => {
            const color = getSeriesColor(index, item.color);

            return (
              <Bar
                key={item.dataKey}
                dataKey={item.dataKey}
                name={item.name}
                fill={`url(#${chartId}-${item.dataKey}-bar)`}
                stackId={stacked ? 'stack' : undefined}
                radius={horizontal
                  ? [0, chartTheme.barRadius, chartTheme.barRadius, 0]
                  : [chartTheme.barRadius, chartTheme.barRadius, 0, 0]}
                animationDuration={animated ? chartAnimation.duration : 0}
                animationBegin={animated ? chartAnimation.begin + (index * chartAnimation.stagger) : 0}
                animationEasing={chartAnimation.easing}
                barSize={item.barSize || chartTheme.barSize}
              >
                {colorByValue && data.map((entry, entryIndex) => (
                  <Cell
                    key={`${item.dataKey}-${entryIndex}`}
                    fill={getColorByValue(Number(entry?.[item.dataKey] || 0))}
                    stroke={alpha(color, 0.08)}
                  />
                ))}
              </Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>

      {showLegend && (
        <InteractiveLegend
          items={series}
          hiddenKeys={hiddenKeys}
          onToggle={toggleSeries}
          isMobile={chartTheme.isMobile}
        />
      )}
    </ChartContainer>
  );
};

export default InteractiveBarChart;
